import * as React from "react";
import type {App} from "./App";
import type {Navigation} from "./Navigation";
import type {TransportLayer} from "./data/TransportLayer";
import type {AppState} from "./data/state/AppState";

export interface AppStores {
	readonly app: App;
	readonly appState: AppState;
	readonly navigation: Navigation;
	readonly transport: TransportLayer;
}

const AppStoresContext = React.createContext<AppStores | null>(null);

export function AppStoresProvider({app, appState, navigation, transport, children}: React.PropsWithChildren<AppStores>) {
	const stores = React.useMemo(() => ({app, appState, navigation, transport}), [app, appState, navigation, transport]);
	return <AppStoresContext.Provider value={stores}>{children}</AppStoresContext.Provider>;
}

// React applies these to the original component; copying them would change the wrapper's behavior.
const componentStatics = new Set<PropertyKey>([
	"$$typeof",
	"render",
	"compare",
	"type",
	"childContextTypes",
	"contextType",
	"contextTypes",
	"defaultProps",
	"getDefaultProps",
	"getDerivedStateFromError",
	"getDerivedStateFromProps",
	"mixins",
	"displayName",
	"propTypes",
	"name",
	"length",
	"prototype",
	"caller",
	"arguments",
]);

export function withAppStores(...keys: (keyof AppStores)[]) {
	return function <T extends React.ComponentType<any>>(Component: T): T {
		const WithAppStores = React.forwardRef<unknown, Record<string, any>>((props, ref) => {
			const stores = React.useContext(AppStoresContext);
			const injectedProps = {...props};
			for (const key of keys) {
				// An explicitly supplied prop, including undefined, takes precedence over context.
				if (key in injectedProps) {
					continue;
				}
				if (!stores || !(key in stores)) {
					throw new Error(`App store '${key}' is not available. Wrap this component in AppStoresProvider or pass the store as a prop.`);
				}
				injectedProps[key] = stores[key];
			}
			if (ref !== null) {
				injectedProps.ref = ref;
			}
			return React.createElement(Component, injectedProps);
		});

		for (const key of Reflect.ownKeys(Component)) {
			if (!componentStatics.has(key)) {
				Object.defineProperty(WithAppStores, key, Object.getOwnPropertyDescriptor(Component, key));
			}
		}
		WithAppStores.displayName = `withAppStores(${Component.displayName || Component.name || "Component"})`;

		// Keep generic class signatures and instance-ref types when used as a legacy class decorator.
		return WithAppStores as unknown as T;
	};
}
